import { describeReceipt } from '@kronos/domain'
import type { InventoryItem, Receipt } from '../../data/inventory'
import { Section } from '../primitives'
import { Link } from '../shell/Link'
import { AddPhotoButton } from '../inventory/AddPhotoButton'
import { ItemImage } from '../inventory/ItemArt'

const SHOWN = 8

/**
 * Step 3 — done. The item's picture and the sentence a person repeats to
 * whoever asked ("22 cameras in, 34 on the shelf"), the numbers Kronos gave
 * them, and the way back to the start for the next box. If the item has no
 * photo yet, this is when the part is in their hand: one button takes one.
 */
export function ReceiveDone({
  item,
  receipt,
  onAnother,
  onMore,
}: {
  item: InventoryItem
  receipt: Receipt & { readonly onShelf: number }
  onAnother: () => void
  onMore: () => void
}): JSX.Element {
  const sentence = describeReceipt({ name: item.name, quantity: receipt.quantity, unit: item.unit, onShelf: receipt.onShelf })
  const [what, after] = sentence.split(' · ')

  return (
    <div className="page page--narrow">
      <div className="panel inv-done" role="status">
        <span className="inv-done__media">
          <ItemImage imageId={item.imageId} category={item.category} name={item.name} />
          <span className="inv-done__mark" aria-hidden="true">
            ✓
          </span>
        </span>
        <span className="inv-done__text">
          <span className="inv-done__what">{what}</span>
          <span className="muted">{after}</span>
          {receipt.note ? <span className="muted">{receipt.note}</span> : null}
        </span>
      </div>

      {item.imageId === null ? (
        <div className="panel panel--padded inv-photo-ask">
          <span>
            <strong>No photo of {item.name} yet.</strong>
            <span className="muted"> Take one now, while it is in your hand — it is the picture everyone will look for.</span>
          </span>
          <AddPhotoButton itemId={item.id} label="Take a photo" />
        </div>
      ) : null}

      <button type="button" className="primary inv-confirm" onClick={onAnother} autoFocus>
        Receive something else
      </button>
      <div className="inv-done__more">
        <button type="button" onClick={onMore}>
          Receive more of these
        </button>
        <Link to={{ name: 'item', id: item.id }} className="button">
          Open item
        </Link>
      </div>

      {receipt.units.length > 0 ? (
        <Section label="Kronos numbers">
          <div className="panel">
            <div className="row-list">
              {receipt.units.slice(0, SHOWN).map((u) => (
                <Link key={u.componentId} to={{ name: 'component', id: u.componentId }} className="row-item">
                  <span className="serial">{u.serial}</span>
                  <span className="row-item__side inv-done__maker">{u.makerSerial ?? 'no maker’s serial'}</span>
                </Link>
              ))}
            </div>
          </div>
          {receipt.units.length > SHOWN ? (
            <p className="muted inv-quiet">and {receipt.units.length - SHOWN} more — all of them are on the item page.</p>
          ) : null}
        </Section>
      ) : null}
    </div>
  )
}
